import { useEffect, useState, useRef } from "react";
import confetti from "canvas-confetti";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
    Snackbar,
    Alert,
    Tooltip,
    Collapse,
    useMediaQuery,
} from "@mui/material";
import {
    ContentPasteOutlined,
    EmailOutlined,
    WhatsApp,
    ReportProblemOutlined,
    KeyboardArrowUp,
    SaveOutlined,
    RefreshOutlined,
    ClearAll,
    KeyboardArrowDown,
    ShareOutlined,
    WarningAmber,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";

import { useQuery, useEncryption } from "../../hooks";
import { useNotepad } from "../../hooks/query";
import { Editor, SimpleEditor, NotepadHeader, LocalNoteStore } from "../../components";
import { Button, Paper } from "../../shared";
import { AdditionalControl } from "../../components";
import { PasswordFirewall } from "./PasswordFirewall";

import "./index.scss";

export default function Notepad() {
    const fetchQueryData = useQuery().get("fetch");
    const { id } = useParams();
    const editorRef = useRef(null);
    const notePadIdRef = useRef<any>(null);
    const languageRef = useRef<any>(null);

    const { encryptData, decryptData } = useEncryption();
    const { useGetNote, useWriteNote } = useNotepad();
    const { isLoading, error, mutateAsync } = useGetNote();
    const writeNote = useWriteNote();

    const [state, setState] = useState("");
    const [changeIdentified, setChangeIdentified] = useState(false);
    const [toastState, setToastState] = useState(false);
    const [notepadLanguage, setNotepadLanguage] = useState("plaintext");
    const [password, setPassword] = useState<any>(null);
    const [passwordFirewall, setPasswordFirewall] = useState<boolean>(false);
    const [additionalControls, setAdditionalControls] = useState({
        password: false,
        wrapText: true,
        readOnly: false,
        groupNote: false,
        monacoMobile: false,
    });
    const isMobile = useMediaQuery("(max-width:700px)");
    let saveStarted = false;

    const handleOnAdditionalControlsChange = (name: string, value: boolean) => {
        setAdditionalControls((current: any) => ({ ...current, [name]: value }));
    };
    const fetchData = async () => {
        try {
            const params = {
                id: id,
                password: password,
            };
            const response = await mutateAsync(params);
            if (response.passwordRequired) {
                setPasswordFirewall(true);
                setPassword(null);
                return;
            }
            setPasswordFirewall(false);
            const decryptedData = decryptData(response.note, id);
            setState(decryptedData);
            setNotepadLanguage(response.language);
        } catch (e: any) {
            const isIncorrectPassword =
                e.response?.status === 401 || e.response?.data?.error?.message === "incorrect password";
            if (isIncorrectPassword) {
                // Keep firewall up if it's a wrong password
                setPasswordFirewall(true);
            } else {
                setToastState(true);
            }
        }
    };

    const handleChange = (value: any) => {
        //setState(value)
        setChangeIdentified(true);
    };

    const handleSave = async (clear: boolean = false) => {
        try {
            if (saveStarted) {
                return;
            }
            //@ts-ignore
            const currentNote = editorRef.current.getValue();
            const encryptedNote = encryptData(currentNote, notePadIdRef.current);
            const params = {
                id: notePadIdRef.current,
                note: clear ? "cleared" : encryptedNote,
                language: notepadLanguage,
                password: password,
            };
            saveStarted = true;
            await writeNote.mutateAsync(params);
            saveStarted = false;
            setChangeIdentified(false);
            setToastState(true);

            // Celebration!
            if (!clear) {
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 },
                    colors: ["#ffb347", "#ffcc33", "#ffffff"],
                });
            }

            if (clear) {
                setState("cleared");
            }
        } catch (e) {
            console.error(e);
            saveStarted = false;
            setToastState(true);
        }
    };

    async function saveButtonClick(e: any) {
        if (e.keyCode === 83 && (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)) {
            e.preventDefault();
            await handleSave();
        }
    }

    useEffect(() => {
        notePadIdRef.current = id;
        fetchData().then();
    }, [fetchQueryData, id]);

    useEffect(() => {
        document.addEventListener("keydown", saveButtonClick, false);
        return () => {
            document.removeEventListener("keydown", saveButtonClick);
        };
    }, [id]);

    useEffect(() => {
        document.documentElement.style.setProperty("--fq-header-max-width", "100%");
        return () => {
            document.documentElement.style.setProperty("--fq-header-max-width", "1200px");
        };
    }, []);

    return (
        <div className="NotePadContent-Wrapper h-full w-full">
            <NotepadHeader notepadId={notePadIdRef.current || "unknown"} warning={changeIdentified} />
            {passwordFirewall && (
                <PasswordFirewall
                    isLoading={isLoading}
                    error={error}
                    incorrectPassword={
                        (error as any)?.response?.status === 401 ||
                        (error as any)?.response?.data?.error?.message === "incorrect password" ||
                        (writeNote.error as any)?.response?.status === 401 ||
                        (writeNote.error as any)?.response?.data?.error?.message === "incorrect password"
                    }
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    onFetch={fetchData}
                />
            )}
            {writeNote.isError && (writeNote.error as any)?.response?.status !== 401 && (
                <SaveStatus
                    open={toastState}
                    handleClose={() => setToastState(false)}
                    severity={"error"}
                    message={`Cannot save/load this note. ${error}`}
                />
            )}
            {writeNote.isSuccess && (
                <SaveStatus
                    open={toastState}
                    handleClose={() => setToastState(false)}
                    severity={"success"}
                    message={"Saved successfully!"}
                />
            )}
            <div className={"notedrop-editor-container w-full"}>
                {(isLoading || writeNote.isLoading) && <LoadingSpinner />}
                <SideNavMobile
                    onPasswordChange={setPassword}
                    language={notepadLanguage}
                    onLanguageChange={(language: any) => {
                        languageRef.current = language;
                        setNotepadLanguage(language);
                    }}
                    onControlChange={handleOnAdditionalControlsChange}
                    control={additionalControls}
                    onRefresh={fetchData}
                    onSave={() => handleSave(false)}
                    onClear={() => handleSave(true)}
                    apiState={writeNote}
                    id={notePadIdRef.current}
                    isMobile={isMobile}
                />
                <Box sx={{ flex: 1, position: "relative" }} className="notepad-editor">
                    {isMobile && !additionalControls.monacoMobile ? (
                        <SimpleEditor
                            ref={editorRef}
                            value={state}
                            options={additionalControls}
                            onValueChange={handleChange}
                        />
                    ) : (
                        <Editor
                            editorRef={editorRef}
                            value={state}
                            options={additionalControls}
                            onValueChange={handleChange}
                            mode={undefined}
                            language={notepadLanguage}
                        />
                    )}
                    {changeIdentified && (
                        <Tooltip title={"Unsaved changes"}>
                            <div className="unsaved-indicator blink_me">
                                <WarningAmber sx={{ fontSize: 16, color: "var(--fq-warning-red)" }} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: "var(--fq-warning-red)", fontWeight: 600, fontSize: 11 }}
                                >
                                    Unsaved
                                </Typography>
                            </div>
                        </Tooltip>
                    )}
                </Box>
                <Divider orientation={"vertical"} className="notepad-editor-divider" />
                <SideNav
                    onPasswordChange={setPassword}
                    language={notepadLanguage}
                    onControlChange={handleOnAdditionalControlsChange}
                    onLanguageChange={(language: any) => {
                        languageRef.current = language;
                        setNotepadLanguage(language);
                    }}
                    onRefresh={fetchData}
                    control={additionalControls}
                    onSave={() => handleSave(false)}
                    onClear={() => handleSave(true)}
                    apiState={writeNote}
                    id={notePadIdRef.current}
                    isMobile={isMobile}
                />
            </div>
        </div>
    );
}

function SideNav(props: any) {
    const btnStyle = {
        textDecoration: "none",
        textTransform: "none",
        borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px",
        flex: 1,
        fontWeight: 600,
        fontSize: 13,
        fontFamily: "var(--fq-font-hand-body)",
        border: "2px solid currentColor",
        boxShadow: "2px 2px 0px var(--fq-primary-text-color)",
        transition: "var(--fq-transition-smooth)",
        "&:hover": {
            transform: "translate(-1px, -1px)",
            boxShadow: "3px 3px 0px var(--fq-primary-text-color)",
        },
        "&:active": {
            transform: "translate(1px, 1px)",
            boxShadow: "1px 1px 0px var(--fq-primary-text-color)",
        },
    };
    return (
        <Box
            sx={{ width: "320px", p: 0, display: "flex", flexDirection: "column", gap: 2 }}
            className={"notepad-sidenav-large"}
        >
            <Stack direction={"row"} spacing={1.5}>
                <Button
                    variant={"contained"}
                    startIcon={
                        props.apiState.isLoading ? (
                            <CircularProgress size={"14px"} />
                        ) : (
                            <SaveOutlined sx={{ fontSize: 18 }} />
                        )
                    }
                    sx={{ ...btnStyle }}
                    onClick={props.onSave}
                    disabled={props.apiState.isLoading}
                >
                    {props.apiState.isLoading ? (
                        <Typography fontSize={"inherit"}>Saving</Typography>
                    ) : (
                        <Typography fontSize={"inherit"}>Save</Typography>
                    )}
                </Button>
                <Button
                    startIcon={<RefreshOutlined sx={{ fontSize: 18 }} />}
                    variant={"contained"}
                    disabled={props.apiState.isLoading}
                    sx={{ ...btnStyle }}
                    color={"primary"}
                    onClick={props.onRefresh}
                >
                    Refresh
                </Button>
                <Button
                    startIcon={<ClearAll sx={{ fontSize: 18 }} />}
                    variant={"contained"}
                    disabled={props.apiState.isLoading}
                    sx={{ ...btnStyle }}
                    color={"error"}
                    onClick={props.onClear}
                >
                    Clear
                </Button>
            </Stack>
            <AdditionalControl {...props} />
            <Box className="share-section">
                <Box display={"flex"} alignItems={"center"} sx={{ p: 1.5, pl: 2 }} gap={1}>
                    <ShareOutlined fontSize={"small"} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--fq-font-handwriting)" }}>
                        Share
                    </Typography>
                </Box>
                <Divider />
                <Typography
                    sx={{
                        maxWidth: "100%",
                        fontWeight: 400,
                        p: 2,
                        whiteSpace: "pre-line",
                        wordWrap: "break-word",
                        fontSize: 14,
                        lineHeight: 1.7,
                        fontFamily: "var(--fq-font-hand-body)",
                        color: "var(--fq-primary-text-color)",
                    }}
                    variant="body2"
                >
                    Share notes effortlessly with NoteDrop!
                    <br />
                    Id: <strong>{props.id}</strong>
                    <br />
                    Let's stay organized together.
                    <br />
                    <br />
                    —NoteDrop
                </Typography>
                <Divider />
                <Stack direction={"row"} gap={1.5} alignItems={"center"} p={1.5} pl={2}>
                    <SocialMediaShare
                        icon={ContentPasteOutlined}
                        onClick={() => navigator.clipboard.writeText(`https://www.nithinpb.com/notedrop/${props.id}`)}
                    />
                    <SocialMediaShare
                        icon={WhatsApp}
                        link={`whatsapp://send?text=Share notes effortlessly with NoteDrop!%0D%0AClick here: https://www.nithinpb.com/notedrop/${props.id}.%0D%0ALet's stay organized together. %0D%0A %0D%0A-NoteDrop`}
                    />
                    <SocialMediaShare
                        icon={EmailOutlined}
                        link={`mailto:?subject=Note Sharing | NoteDrop ${props.id}&body=Share notes effortlessly with NoteDrop!%0D%0AClick here: https://www.nithinpb.com/notedrop/${props.id}.%0D%0ALet's stay organized together. %0D%0A %0D%0A-NoteDrop`}
                    />
                </Stack>
            </Box>
        </Box>
    );
}

function SideNavMobile(props: any) {
    const [openExtra, setOpenExtra] = useState(false);

    const btnStyle = {
        textDecoration: "none",
        textTransform: "none",
        borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px",
        fontWeight: 600,
        fontSize: 13,
        fontFamily: "var(--fq-font-hand-body)",
        border: "2px solid currentColor",
        boxShadow: "2px 2px 0px var(--fq-primary-text-color)",
    };

    const handleExtra = () => {
        setOpenExtra((current: any) => !current);
    };

    return (
        <Box
            sx={{ p: 2, display: "flex", flexDirection: "column", gap: openExtra ? 2 : 0 }}
            className={"notepad-sidenav-small"}
        >
            <Collapse in={openExtra} orientation={"vertical"}>
                <Box>
                    <Box className="share-section" sx={{ mb: 2 }}>
                        <Box display={"flex"} alignItems={"center"} sx={{ p: 1.5, pl: 2 }} gap={1}>
                            <ShareOutlined fontSize={"small"} />
                            <Typography
                                sx={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--fq-font-handwriting)" }}
                            >
                                Share
                            </Typography>
                        </Box>
                        <Divider />
                        <Typography
                            sx={{
                                maxWidth: "100%",
                                fontWeight: 400,
                                p: 2,
                                whiteSpace: "pre-line",
                                wordWrap: "break-word",
                                fontSize: 14,
                                lineHeight: 1.7,
                                fontFamily: "var(--fq-font-hand-body)",
                                color: "var(--fq-primary-text-color)",
                            }}
                            variant="body2"
                        >
                            Share notes effortlessly with NoteDrop!
                            <br />
                            Id: <strong>{props.id}</strong>
                            <br />
                            Let's stay organized together.
                            <br />
                            <br />
                            —NoteDrop
                        </Typography>
                        <Divider />
                        <Stack direction={"row"} gap={1.5} alignItems={"center"} p={1.5} pl={2}>
                            <SocialMediaShare
                                icon={ContentPasteOutlined}
                                onClick={() =>
                                    navigator.clipboard.writeText(`https://www.nithinpb.com/notedrop/${props.id}`)
                                }
                            />
                            <SocialMediaShare
                                icon={WhatsApp}
                                link={`whatsapp://send?text=Share notes effortlessly with NoteDrop!%0D%0AClick here: https://www.nithinpb.com/notedrop/${props.id}.%0D%0ALet's stay organized together. %0D%0A %0D%0A-NoteDrop`}
                            />
                            <SocialMediaShare
                                icon={EmailOutlined}
                                link={`mailto:?subject=Note Sharing | NoteDrop ${props.id}&body=Share notes effortlessly with NoteDrop!%0D%0AClick here: https://www.nithinpb.com/notedrop/${props.id}.%0D%0ALet's stay organized together. %0D%0A %0D%0A-NoteDrop`}
                            />
                        </Stack>
                    </Box>
                    <AdditionalControl {...props} />
                </Box>
            </Collapse>
            <Stack direction={"row"} spacing={1} justifyContent={"space-between"} alignItems={"center"}>
                <Stack direction={"row"} spacing={1} justifyContent={"flex-start"}>
                    <Button
                        variant={"contained"}
                        sx={{ ...btnStyle, px: 2 }}
                        onClick={props.onSave}
                        startIcon={<SaveOutlined sx={{ fontSize: 16 }} />}
                        disabled={props.apiState.isLoading}
                    >
                        {props.apiState.isLoading ? (
                            <Typography fontSize={"inherit"}>Saving...</Typography>
                        ) : (
                            <Typography fontSize={"inherit"}>Save</Typography>
                        )}
                    </Button>
                    <Button
                        variant={"contained"}
                        disabled={props.apiState.isLoading}
                        sx={{ ...btnStyle, minWidth: 42, px: 0 }}
                        color={"primary"}
                        onClick={props.onRefresh}
                        title="Refresh"
                    >
                        <RefreshOutlined sx={{ fontSize: 18 }} />
                    </Button>
                    <Button
                        variant={"contained"}
                        disabled={props.apiState.isLoading}
                        sx={{ ...btnStyle, minWidth: 42, px: 0 }}
                        color={"error"}
                        onClick={props.onClear}
                        title="Clear"
                    >
                        <ClearAll sx={{ fontSize: 18 }} />
                    </Button>
                </Stack>
                <IconButton
                    onClick={handleExtra}
                    sx={{
                        borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px",
                        width: 40,
                        height: 40,
                        border: "var(--fq-sketch-border)",
                        boxShadow: "2px 2px 0px var(--fq-primary-text-color)",
                        color: "var(--fq-primary-text-color)",
                        transition: "var(--fq-transition-smooth)",
                        "&:hover": {
                            transform: "rotate(10deg)",
                        },
                    }}
                >
                    {openExtra ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
            </Stack>
        </Box>
    );
}

function SocialMediaShare(props: any) {
    const { icon: Icon, link, onClick = () => {} }: { icon: any; link: any; onClick: any } = { ...props };
    return (
        <Box>
            <a href={link}>
                <IconButton
                    size={"small"}
                    onClick={onClick}
                    sx={{
                        borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px",
                        width: 36,
                        height: 36,
                        border: "var(--fq-sketch-border)",
                        color: "var(--fq-primary-text-color)",
                        boxShadow: "2px 2px 0px var(--fq-primary-text-color)",
                        transition: "var(--fq-transition-smooth)",
                        "&:hover": {
                            transform: "scale(1.08) rotate(5deg)",
                            boxShadow: "3px 3px 0px var(--fq-primary-text-color)",
                        },
                    }}
                >
                    <Icon sx={{ fontSize: 18, color: "var(--fq-primary-text-color)" }} />
                </IconButton>
            </a>
        </Box>
    );
}

function LoadingSpinner() {
    return (
        <div
            style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 15000,
                borderRadius: "var(--fq-card-radius)",
                overflow: "hidden",
            }}
        >
            <LinearProgress sx={{ borderRadius: 2 }} />
        </div>
    );
}

function SaveStatus(props: any) {
    return (
        <Snackbar
            open={props.open}
            autoHideDuration={2000}
            onClose={props.handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
            <Alert
                onClose={props.handleClose}
                severity={props.severity}
                sx={{
                    width: "100%",
                    borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px",
                    border: `2px solid ${props.severity === "error" ? "var(--fq-warning-red)" : "var(--fq-accent-mint)"}`,
                    boxShadow: "3px 3px 0px var(--fq-primary-text-color)",
                    fontWeight: 600,
                    fontFamily: "var(--fq-font-hand-body)",
                }}
            >
                {props.message}
            </Alert>
        </Snackbar>
    );
}
